import React, { useEffect, useMemo, useRef, useState } from "react";
import { NAV_FEATURES, NAV_ITEMS, NAV_LEGACY_PRODUCT_PATHS, NAV_STRATEGY_WALLET_CLUSTER } from "../config/navigation";
import { SITE } from "../config/site";
import Link from "../components/Link";
import HeaderAuthControls from "../components/HeaderAuthControls";
import useRoute from "../hooks/useRoute";

function normalizePath(p) {
  const raw = (p || "/").trim();
  const cleaned = raw.replace(/\/+$/, "");
  return cleaned.length ? cleaned : "/";
}

function FeatureIcon({ name }) {
  const wrap =
    "relative mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-hint/10 ring-1 ring-hint/20 transition group-hover:bg-hint/20";
  const svgClass = "relative h-5 w-5 text-hint-deep";
  const common = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };

  const paths = {
    bot: (
      <>
        <rect x="5" y="8" width="14" height="11" rx="3" />
        <path d="M12 8V5" />
        <circle cx="9" cy="13" r="1" fill="currentColor" stroke="none" />
        <circle cx="15" cy="13" r="1" fill="currentColor" stroke="none" />
        <path d="M9 17h6" />
      </>
    ),
    builder: (
      <>
        <path d="M7 7h10" />
        <path d="M7 17h10" />
        <path d="M10 7v10" />
        <path d="M14 7v10" />
        <path d="M9 12h6" />
      </>
    ),
    discovery: (
      <>
        <circle cx="11" cy="11" r="6.5" />
        <path d="M16 16l4.5 4.5" />
        <path d="M11 8v6" />
        <path d="M8 11h6" />
      </>
    ),
  };

  return (
    <span aria-hidden="true" className={wrap}>
      <svg viewBox="0 0 24 24" className={svgClass} {...common}>
        {paths[name] || null}
      </svg>
    </span>
  );
}

function AppleMark({ className = "h-3.5 w-3.5" }) {
  return (
    <svg viewBox="0 0 384 512" aria-hidden="true" className={className} fill="currentColor">
      <path d="M318.7 268.6c-.3-49.2 40.4-72.8 42.2-74-23-33.6-58.7-38.2-71.3-38.7-30.4-3.1-59.3 17.9-74.7 17.9-15.4 0-39.2-17.4-64.5-16.9-33.1 .5-63.6 19.2-80.5 48.8-34.4 59.6-8.8 147.8 24.7 196.2 16.4 23.7 36 50.3 61.7 49.3 24.8-1 34.2-16 64.1-16 29.8 0 38.3 16 64.6 15.5 26.7-.5 43.6-24.2 59.9-48 18.9-27.6 26.7-54.3 27-55.7-.6-.3-51.7-19.9-52-78.7zM265.1 114.1c13.7-16.6 22.9-39.7 20.4-62.8-19.7 .8-43.6 13.1-57.8 29.7-12.7 14.7-23.8 38.3-20.8 60.9 22.1 1.7 44.5-11.2 58.2-27.8z" />
    </svg>
  );
}

export default function SiteHeader() {
  const route = useRoute();
  const activePath = route.path || "/";
  const [openProduct, setOpenProduct] = useState(false);
  const [openMobile, setOpenMobile] = useState(false);
  const [openMobileProduct, setOpenMobileProduct] = useState(false);
  const productRef = useRef(null);

  const featureMenu = useMemo(() => NAV_FEATURES, []);

  const featuresPaths = useMemo(() => {
    const base = NAV_FEATURES.map((x) => normalizePath(x.href));
    const cluster = (NAV_STRATEGY_WALLET_CLUSTER || []).map((x) => normalizePath(x.href));
    const legacy = (NAV_LEGACY_PRODUCT_PATHS || []).map((x) => normalizePath(x));
    return [...base, ...cluster, ...legacy];
  }, []);

  const isProductActive = featuresPaths.includes(normalizePath(activePath));
  const isConnectActive = normalizePath(activePath) === "/connect-brokerage";
  const isPricingActive = normalizePath(activePath) === "/pricing" || normalizePath(activePath) === "/stratifypro";

  useEffect(() => {
    setOpenProduct(false);
    setOpenMobile(false);
    setOpenMobileProduct(false);
  }, [activePath]);

  useEffect(() => {
    if (!openMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [openMobile]);

  useEffect(() => {
    const onMouseDown = (e) => {
      if (productRef.current && !productRef.current.contains(e.target)) {
        setOpenProduct(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setOpenProduct(false);
        setOpenMobile(false);
        setOpenMobileProduct(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const closeMobile = () => {
    setOpenMobile(false);
    setOpenMobileProduct(false);
  };

  const navLinkClass = (active) =>
    [
      "relative rounded-full px-3 py-2 text-[13px] font-medium tracking-tight transition",
      active ? "text-ink" : "text-muted hover:text-ink",
    ].join(" ");

  return (
    <header className="fixed left-0 right-0 top-0 z-50 border-b border-ink/[0.06] bg-white/90 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="group flex items-center gap-2.5" aria-label={`${SITE.name} Home`}>
            <img
              src="/images/StratifyLogo.png"
              alt=""
              className="h-8 w-8 rounded-xl object-contain ring-1 ring-ink/5"
              loading="eager"
            />
            <span className="text-[15px] font-semibold tracking-tight text-ink">Stratify</span>
          </Link>

          <nav className="hidden items-center gap-2 md:flex" aria-label="Primary">
            <div className="relative" ref={productRef}>
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={openProduct}
                className={navLinkClass(isProductActive)}
                onClick={() => setOpenProduct((v) => !v)}
              >
                <span className="flex items-center gap-1.5">
                  Product
                  <svg
                    className={["h-4 w-4 transition", openProduct ? "rotate-180" : ""].join(" ")}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              </button>

              <div
                role="menu"
                className={[
                  "absolute left-0 top-[calc(100%+12px)] w-[340px] overflow-hidden rounded-[24px]",
                  "border border-ink/8 bg-white/95 shadow-device backdrop-blur-xl transition origin-top-left",
                  openProduct ? "pointer-events-auto scale-100 opacity-100" : "pointer-events-none scale-[0.98] opacity-0",
                ].join(" ")}
              >
                <div className="p-2">
                  {featureMenu.map((item) => {
                    const isActive = activePath === normalizePath(item.href);
                    return (
                      <Link
                        key={item.key}
                        to={item.href}
                        role="menuitem"
                        aria-current={isActive ? "page" : undefined}
                        className={[
                          "group flex items-start gap-3 rounded-2xl px-3 py-2.5 text-sm transition",
                          isActive ? "bg-bg1 text-ink" : "text-ink/80 hover:bg-bg1",
                        ].join(" ")}
                        onClick={() => setOpenProduct(false)}
                      >
                        <FeatureIcon name={item.icon} />
                        <div className="min-w-0">
                          <div className="font-medium">{item.label}</div>
                          <div className="mt-0.5 text-[12px] text-muted">{item.desc}</div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            <Link
              to="/connect-brokerage"
              aria-current={isConnectActive ? "page" : undefined}
              className={navLinkClass(isConnectActive)}
            >
              How to connect your brokerage account
            </Link>
            <Link
              to="/pricing"
              aria-current={isPricingActive ? "page" : undefined}
              className={navLinkClass(isPricingActive)}
            >
              Pricing
            </Link>
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-full p-2 text-ink ring-1 ring-transparent hover:bg-bg1 md:hidden"
              aria-label={openMobile ? "Close menu" : "Open menu"}
              aria-expanded={openMobile}
              onClick={() => setOpenMobile((v) => !v)}
            >
              {openMobile ? (
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
                  <path d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12 5.7 16.89a1 1 0 1 0 1.41 1.41L12 13.41l4.89 4.89a1 1 0 0 0 1.41-1.41L13.41 12l4.89-4.89a1 1 0 0 0 0-1.4Z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
                  <path d="M4 6.5A1 1 0 0 1 5 5.5h14a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1Zm0 5.5A1 1 0 0 1 5 11h14a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1Zm1 4.5a1 1 0 1 0 0 2h14a1 1 0 1 0 0-2H5Z" />
                </svg>
              )}
            </button>

            <HeaderAuthControls variant="desktop" />

            <a
              href={SITE.appStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3.5 py-2 text-[13px] font-semibold text-white transition hover:bg-ink/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink md:px-4"
            >
              <span className="hidden sm:inline">Get the app</span>
              <span className="sm:hidden">Get</span>
              <span aria-hidden="true">↗</span>
            </a>
          </div>
        </div>
      </div>

      <div className={["md:hidden", openMobile ? "pointer-events-auto" : "pointer-events-none"].join(" ")}>
        <div
          className={[
            "fixed inset-x-0 top-16 h-[calc(100vh-64px)] bg-ink/20 backdrop-blur-sm transition",
            openMobile ? "opacity-100" : "opacity-0",
          ].join(" ")}
          onClick={closeMobile}
          aria-hidden="true"
        />
        <div
          className={[
            "relative mx-auto max-w-7xl px-4 transition",
            openMobile ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0",
          ].join(" ")}
        >
          <div className="mt-3 overflow-hidden rounded-[28px] border border-ink/8 bg-white shadow-device">
            <div className="max-h-[calc(100vh-96px)] space-y-1 overflow-y-auto p-3">
              <button
                type="button"
                className="flex w-full items-center justify-between rounded-2xl px-4 py-3 text-left text-[15px] font-medium text-ink hover:bg-bg1"
                aria-expanded={openMobileProduct}
                onClick={() => setOpenMobileProduct((v) => !v)}
              >
                Product
                <svg
                  className={["h-4 w-4 transition", openMobileProduct ? "rotate-180" : ""].join(" ")}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {openMobileProduct
                ? featureMenu.map((item) => (
                    <Link
                      key={item.key}
                      to={item.href}
                      className="block rounded-2xl px-4 py-3 text-sm text-muted hover:bg-bg1 hover:text-ink"
                      onClick={closeMobile}
                    >
                      <div className="font-medium text-ink">{item.label}</div>
                      <div className="mt-0.5 text-xs">{item.desc}</div>
                    </Link>
                  ))
                : null}

              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.key}
                  to={item.href}
                  className="block rounded-2xl px-4 py-3 text-[15px] font-medium text-ink hover:bg-bg1"
                  onClick={closeMobile}
                >
                  {item.label}
                </Link>
              ))}

              <div className="space-y-3 border-t border-ink/8 p-2 pt-4">
                <HeaderAuthControls variant="mobile" onAction={closeMobile} />
                <a
                  href={SITE.appStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-ink px-4 py-3 text-sm font-semibold text-white"
                  onClick={closeMobile}
                >
                  <AppleMark />
                  Get Stratify for iPhone
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
